import React, { useEffect, useState } from 'react';
import { storage } from '../../services/StorageService';

/**
 * ColdCloneBanner — surfaces a non-blocking warning when the browser's
 * localStorage is unavailable (private-mode tab, quota exhausted, etc).
 *
 * The game is fully playable in this state; only the persistence layer
 * is off. Without a banner, the user would silently lose every preference
 * change after a reload — much worse than telling them upfront.
 *
 * Mount once at app root (next to the game canvas). Self-dismissing after
 * 8s; the user can also close it manually. Stays out of the way otherwise.
 */
export const ColdCloneBanner: React.FC = () => {
  const [hidden, setHidden] = useState(false);

  // Re-evaluate on mount so SSR/test environments without window pass through
  // cleanly. The probe is cached inside StorageService so the cost is one
  // setItem on first call.
  const persistent = storage.isPersistent();

  useEffect(() => {
    if (persistent) return;
    const t = setTimeout(() => setHidden(true), 8000);
    return () => clearTimeout(t);
  }, [persistent]);

  if (persistent || hidden) return null;

  return (
    <div
      role="alert"
      style={{
        position: 'fixed',
        top: 12,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        background: 'rgba(241, 196, 15, 0.95)',
        color: '#1a1a1a',
        padding: '8px 16px',
        borderRadius: 6,
        fontFamily: 'Inter, sans-serif',
        fontSize: 13,
        boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <span>
        <strong>Heads-up:</strong> your browser blocked persistent storage.
        Settings won't survive this tab close.
      </span>
      <button
        type="button"
        onClick={() => setHidden(true)}
        style={{
          background: 'transparent',
          border: '1px solid rgba(0,0,0,0.4)',
          color: '#1a1a1a',
          padding: '2px 8px',
          borderRadius: 4,
          cursor: 'pointer',
          fontSize: 12,
        }}
      >
        Dismiss
      </button>
    </div>
  );
};
